// --------------------------------
// Copyright (c) 2023 Eco8 S.r.l.
// --------------------------------
import React, { useState, useEffect } from "react";
import { Text, TextInput, TouchableOpacity, View, Image, ActivityIndicator } from "react-native";
import { authenticationRequest } from "../../axios/axiosConfig";
import colors from "../../config/colors";
import { textboxStyles } from "../../styles/Farmer/textboxStyles";
import styles from "../../styles/LoginScreenStyle";
import { FontAwesome } from "@expo/vector-icons";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { updateLanguage } from "../../utils/utils";
import CountryFlag from 'react-native-country-flag';
import HeaderStyle from "../../styles/HeaderStyle";

const LoginScreen = (props) => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showError, setShowError] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [language, setLanguage] = useState("DE");

    const logger = require("../../axios/loggingConfig");

    useEffect(() => {
        const loadLanguagePreference = async () => {
            try {
                const storedLanguage = await AsyncStorage.getItem("preferredLanguage");
                if (storedLanguage) {
                    setLanguage(storedLanguage);
                } else {
                    await AsyncStorage.setItem("preferredLanguage", "DE");
                }
            } catch (error) {
                console.error("Error loading language preference:", error);
            }
        };
        loadLanguagePreference();
    }, []);

    const handleLogin = async () => {
        if (!username || !password) {
            setSubmitted(true);
            return;
        }

        setIsLoading(true);

        try {
            const requestData = {
                rememberMe: true,
                password: password,
                userName: username,
            };

            const response = await authenticationRequest(requestData);
            setIsLoading(false);

            if (response.status === 200) {
                setSubmitted(true);
                props.onLogin(
                    response.data.firstName,
                    response.data.lastName,
                    response.data.landingView,
                    response.data.Image
                );
            } else {
                setShowError(true);
            }
        } catch (error) {
            setIsLoading(false);
            setShowError(true);
            logger.error("Error on authenticationRequest(): " + error);
        }
    };

    const translations = {
        "Password è un campo obbligatorio": {
            DE: "Passwort ist ein Pflichtfeld",
            IT: "Password è un campo obbligatorio",
        },
        "Username è un campo obbligatorio": {
            DE: "Benutzername ist ein Pflichtfeld",
            IT: "Username è un campo obbligatorio",
        },
        "Accedi": {
            DE: "Be­stä­ti­gen",
            IT: "Accedi",
        },
        "Username o Password errati. Riprova": {
            DE: "Benutzername oder Passwort falsch. Versuchen Sie es erneut",
            IT: "Username o Password errati. Riprova",
        },
    };

    const getTranslation = (key) => {
        const upperLanguage = language || "DE";
        return (translations[key] && translations[key][upperLanguage]) || key;
    };

    const PasswordError = getTranslation("Password è un campo obbligatorio");
    const UserError = getTranslation("Username è un campo obbligatorio");
    const ACCEDI = getTranslation("Accedi");
    const forgotPassword = getTranslation("Hai dimenticato la password?");
    const incorrectCredentials = getTranslation(
        "Username o Password errati. Riprova"
    );

    const languages = [
        { code: "DE", name: "German", flag: "DE" },
        { code: "IT", name: "Italian", flag: "IT" },
    ];

    const handleLanguageChange = async (selectedLanguage) => {
        setLanguage(selectedLanguage);
        try {
            updateLanguage(selectedLanguage);
            await AsyncStorage.setItem("preferredLanguage", selectedLanguage);
        } catch (error) {
            console.error("Error saving language preference:", error);
        }
    };

    const renderLanguageSwitch = () => (
        <View style={HeaderStyle.languageSwitchContainer}>
            {languages.map((lang) => (
                <TouchableOpacity
                    key={lang.code}
                    style={[
                        HeaderStyle.languageSwitchButton,
                        language === lang.code && HeaderStyle.languageSwitchButtonActive,
                    ]}
                    onPress={() => handleLanguageChange(lang.code)}
                >
                    <CountryFlag isoCode={lang.flag} style={HeaderStyle.languageFlag} />
                </TouchableOpacity>
            ))}
        </View>
    );

    return (
        <View style={styles.container}>
            <Image
                style={styles.logo}
                source={require("../../logos/eco8-negativ-A.png")}
            />
            {renderLanguageSwitch()}

            <View style={styles.form}>
                <TextInput
                    style={[
                        textboxStyles.input,
                        { backgroundColor: colors.green, color: colors.white },
                    ]}
                    placeholder="Username"
                    value={username}
                    onChangeText={setUsername}
                />
                {submitted && !username && (
                    <Text style={styles.error}>{UserError}</Text>
                )}
                <View>
                    <TextInput
                        style={[
                            textboxStyles.input,
                            { backgroundColor: colors.green, color: colors.white },
                        ]}
                        placeholder="Password"
                        secureTextEntry={!showPassword}
                        value={password}
                        onChangeText={setPassword}
                    />
                    <TouchableOpacity
                        style={styles.eyeIcon}
                        onPress={() => setShowPassword(!showPassword)}
                    >
                        <FontAwesome
                            name={showPassword ? "eye" : "eye-slash"}
                            size={20}
                            color={colors.white}
                        />
                    </TouchableOpacity>
                </View>

                {submitted && !password && (
                    <Text style={styles.error}>{PasswordError}</Text>
                )}
                <TouchableOpacity
                    style={[styles.button, { backgroundColor: colors.white }]}
                    onPress={() => handleLogin()}
                >
                    {isLoading ? (
                        <ActivityIndicator color={colors.green} />
                    ) : (
                        <Text style={[styles.buttonText, { color: colors.green }]}>
                            {ACCEDI}
                        </Text>
                    )}
                </TouchableOpacity>

                {showError && <Text style={styles.error}>{incorrectCredentials}</Text>}
            </View>
        </View>
    );
};

export default LoginScreen;
